import { useState, useEffect } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Table from '@cloudscape-design/components/table';
import Link from '@cloudscape-design/components/link';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import LineChart from '@cloudscape-design/components/line-chart';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Alert from '@cloudscape-design/components/alert';
import { useNavigate } from 'react-router-dom';
import { getSLAComplianceReport } from '../../api/tickets';
import { SLAComplianceReport as SLAData, SLABreachedTicket } from '../../types/ticket';
import { downloadCsv, csvDateStamp } from '../../utils/csvExport';
import { SeverityBadge } from '../SeverityBadge';

function TicketLink({ ticketId }: { ticketId: string }) {
  const navigate = useNavigate();
  return (
    <Link onFollow={(e) => { e.preventDefault(); navigate(`/tickets/${ticketId}`); }}>
      {ticketId}
    </Link>
  );
}

const BREACHED_COLUMNS = [
  { id: 'ticketId', header: 'Ticket', cell: (item: SLABreachedTicket) => <TicketLink ticketId={item.ticketId} /> },
  { id: 'title', header: 'Title', cell: (item: SLABreachedTicket) => item.title || '—' },
  { id: 'severity', header: 'Severity', cell: (item: SLABreachedTicket) => item.originalSeverity ? <SeverityBadge severity={item.originalSeverity} /> : '—' },
  { id: 'mttr', header: 'Resolution Time', cell: (item: SLABreachedTicket) => `${item.mttrDays}d` },
  { id: 'target', header: 'SLA Target', cell: (item: SLABreachedTicket) => `${item.targetDays}d` },
  {
    id: 'exceeded',
    header: 'Exceeded By',
    cell: (item: SLABreachedTicket) => (
      <StatusIndicator type="error">+{item.exceededByDays}d</StatusIndicator>
    ),
  },
];

export function SLAComplianceReport() {
  const [report, setReport] = useState<SLAData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSLAComplianceReport()
      .then(setReport)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = () => {
    if (!report) return;
    const rows = report.breached.map((t) => [
      t.ticketId, t.title, t.originalSeverity, t.mttrDays, t.targetDays, t.exceededByDays,
    ]);
    downloadCsv(
      `sla-breaches-${csvDateStamp()}.csv`,
      ['ticketId', 'title', 'severity', 'resolutionDays', 'targetDays', 'exceededByDays'],
      rows,
    );
  };

  if (loading) return <Box textAlign="center" padding="xl"><Spinner size="large" /></Box>;
  if (error) return <Alert type="error">Failed to load report: {error}</Alert>;
  if (!report) return <Alert type="info">No report data available.</Alert>;

  const overallPct = report.overall.rate !== null ? Math.round(report.overall.rate * 100) : null;

  const trendSeries: any[] = [
    {
      title: 'Compliance Rate (%)',
      type: 'line',
      data: report.trend
        .filter((w) => w.rate !== null)
        .map((w) => ({ x: w.weekStart, y: Math.round((w.rate ?? 0) * 100) })),
      color: '#0972d3',
    },
    { title: 'Target (90%)', type: 'threshold', y: 90, color: '#037f0c' },
  ];

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description={`Based on tickets resolved in the last ${report.windowDays} days. SLA targets: ${Object.entries(report.slaTargets).map(([sev, days]) => `${sev.replace('_', ' ')}: ${days}d`).join(', ')}`}
            actions={
              <Button iconName="download" onClick={handleDownload}>
                Download CSV
              </Button>
            }
          >
            SLA Compliance
          </Header>
        }
      >
        <ColumnLayout columns={2}>
          <div>
            <Box variant="awsui-key-label">Overall Compliance</Box>
            {overallPct === null ? (
              <Box color="text-body-secondary">No resolved tickets to evaluate</Box>
            ) : (
              <>
                <Box fontSize="display-l" fontWeight="bold" color={overallPct >= 90 ? 'text-status-success' : overallPct >= 70 ? 'text-status-warning' : 'text-status-error'}>
                  {overallPct}%
                </Box>
                <Box variant="small" color="text-body-secondary">
                  {report.overall.compliant} of {report.overall.total} tickets resolved within SLA
                </Box>
              </>
            )}
          </div>
          <div>
            <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>By Severity</Box>
            <SpaceBetween size="xs">
              {Object.entries(report.bySeverity)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([sev, stats]) => (
                  <ProgressBar
                    key={sev}
                    value={stats.rate !== null ? Math.round(stats.rate * 100) : 0}
                    label={`${sev.replace('_', ' ')} (target ${stats.target}d)`}
                    description={`${stats.compliant}/${stats.total} compliant`}
                  />
                ))}
            </SpaceBetween>
          </div>
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h3">8-Week Compliance Trend</Header>}>
        <LineChart
          series={trendSeries}
          yDomain={[0, 110]}
          yTitle="%"
          height={200}
          hideFilter
          xScaleType="categorical"
          i18nStrings={{
            yTickFormatter: (v) => `${Math.round(Number(v))}%`,
          }}
          empty={<Box>No trend data</Box>}
        />
      </Container>

      <Container header={<Header variant="h3" counter={`(${report.breached.length})`}>SLA Breaches</Header>}>
        <Table
          items={report.breached}
          columnDefinitions={BREACHED_COLUMNS}
          variant="embedded"
          empty={<Box color="text-body-secondary">No SLA breaches 🎉</Box>}
          trackBy="ticketId"
        />
      </Container>

      <Box variant="small" color="text-body-secondary" textAlign="right">
        Generated: {new Date(report.generatedAt).toLocaleString()}
      </Box>
    </SpaceBetween>
  );
}
